import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getMesaByToken, getComandaByMesa } from '../services/api';
import { useCarrinho } from '../services/CarrinhoContext';
import Cardapio from './Cardapio';
import Loading from '../components/Loading';

const POLLING_INTERVAL = 10000; // 10 segundos

function TabletTokenDaMesa({ brand }) {
  const { token } = useParams();
  const { setMesa, setTipoEntrega, setComandaExistente, setUltimaComanda, setComanda, limparCarrinho, limparCarrinhoStorage } = useCarrinho();
  const [mesaData, setMesaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollingRef = useRef(null);
  const lastComandaIdRef = useRef(null);

  useEffect(() => {
    if (brand && token) {
      carregarMesa();
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [brand, token]);

  // Polling: verifica a cada 10s se a comanda ainda está ativa
  useEffect(() => {
    if (!mesaData || !brand || !token) return;

    const cleanToken = token.replace(/[^a-zA-Z0-9]/g, '');

    const verificarStatus = async () => {
      try {
        const resultado = await getComandaByMesa(brand, cleanToken);

        if (!resultado || !resultado.comanda || 
            resultado.comanda.status === 'fechada' || 
            resultado.comanda.status === 'cancelada' ||
            resultado.comanda.status === 'entregue') {
          
          // Comanda foi fechada no PDV — resetar tudo
          if (lastComandaIdRef.current) {
            resetarMesa();
          }
        } else {
          // Comanda ativa — manter sincronizado
          const comanda = resultado.comanda;
          lastComandaIdRef.current = comanda.id;
          setComandaExistente(comanda);
          setUltimaComanda(comanda);
          setComanda({ id: comanda.id });
          localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comanda));
        }
      } catch (e) {
        // Erro de rede — não fazer nada, tentar de novo no próximo ciclo
      }
    };

    pollingRef.current = setInterval(verificarStatus, POLLING_INTERVAL);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [mesaData, brand, token]);

  const resetarMesa = () => {
    lastComandaIdRef.current = null;
    localStorage.removeItem('sitri_ultima_comanda');
    localStorage.removeItem('sitri_carrinho');
    setComandaExistente(null);
    setUltimaComanda(null);
    setComanda(null);
    limparCarrinho();
    limparCarrinhoStorage();
  };

  const carregarMesa = async () => {
    try {
      setLoading(true);
      const cleanToken = token.replace(/[^a-zA-Z0-9]/g, '');
      
      const resultado = await getMesaByToken(brand, cleanToken);
      
      if (!resultado) {
        setError('Mesa não encontrada');
        return;
      }

      setMesaData(resultado);
      setMesa(resultado);
      setTipoEntrega('local');

      // Verificar se já existe comanda aberta para esta mesa
      if (resultado.status === 'aberta') {
        resetarMesa();
      } else {
        try {
          const comandaExistente = await getComandaByMesa(brand, cleanToken);
          
          if (comandaExistente && comandaExistente.comanda && 
              comandaExistente.comanda.status !== 'fechada' && 
              comandaExistente.comanda.status !== 'entregue') {
            lastComandaIdRef.current = comandaExistente.comanda.id;
            setComandaExistente(comandaExistente.comanda);
            setUltimaComanda(comandaExistente.comanda);
            setComanda({ id: comandaExistente.comanda.id });
            localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comandaExistente.comanda));
          } else {
            resetarMesa();
          }
        } catch (e) {
          // Se falhar ao buscar comanda, continuar sem ela
        }
      }
      
    } catch (error) {
      setError('Erro ao carregar dados da mesa');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading message="Carregando mesa..." />;
  }

  if (error) {
    return (
      <div className="container mt-5 text-center">
        <div className="alert alert-danger">
          <h4>Erro</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!mesaData) {
    return (
      <div className="container mt-5 text-center">
        <div className="alert alert-warning">
          <h4>Mesa não encontrada</h4>
        </div>
      </div>
    );
  }

  return <Cardapio brand={brand} mesaData={mesaData} />;
}

export default TabletTokenDaMesa;
